/**
 * First Created: 21 Dec 2025
 * Refactored: 06 Jan 2026
 * Author: Atul Pandey
 * Updated - 07 Jan 2026 - name and username changes in the collective contributors array
 *
 * Purpose:
 * Build Action-level performance snapshots from
 * user-normalized collective groups.
 *
 * Scope:
 * - Self → Action
 * - Collective → Action
 *
 * IMPORTANT:
 * - Assumes normalizeCollectiveActions() has already run
 * - No relationship discovery happens here
 */
export function buildPerformanceIndex({
  collectiveGroups = [],
  selfActions = [],
}) {
  const index = {
    Self: {
      Action: new Map(),
    },
    Collective: {
      Action: new Map(),
    },
  };

  // ---------------------------------------------------
  // 1. SELF ACTION PERFORMANCE
  // ---------------------------------------------------
  selfActions.forEach(action => {
    const {
      _id,
      actionGoalRole,
      actualValue = 0,
      targetValue = 0,
      actionUoM = null,
    } = action;

    const progressPercent =
      targetValue > 0
        ? Math.min(100, Math.round((actualValue / targetValue) * 100))
        : 0;

    index.Self.Action.set(String(_id), {
      scope: "Self",
      level: "Action",
      role: actionGoalRole,
      achievedValue: actualValue,
      targetValue,
      uom: actionUoM,
      progressPercent,
      contributors: [],
      sourceActionIds: [String(_id)],
    });
  });

  // ---------------------------------------------------
  // 2. COLLECTIVE ACTION PERFORMANCE
  // ---------------------------------------------------
  collectiveGroups.forEach(group => {
    const {
      anchorActionId,
      anchorAction,
      role, // Sponsor | Assignee (logged-in user's role)
      collectiveActions,
    } = group;

    const {
      actionGoalRole,
      targetValue = 0,
      actionUoM = null,
    } = anchorAction;

    let collectiveAchieved = 0;
    const contributors = [];

    collectiveActions.forEach(action => {
      const actual = Number(action.actualValue || 0);
      collectiveAchieved += actual;

      contributors.push({
        userId: action.userId?._id,
        // userName: action.userId?.name || action.userId?.username, 
        userName: action.userId?.name || "Unknown Name",              // 07 Jan 2026
        username: action.userId?.username || "Unknown username",      // 07 Jan 2026
        actualValue: actual,
        percentOfTarget:
          targetValue > 0
            ? Math.round((actual / targetValue) * 100)
            : 0,
        isAnchor: String(action._id) === String(anchorActionId),
      });
    });

    const collectiveProgress =
      targetValue > 0
        ? Math.min(
            100,
            Math.round((collectiveAchieved / targetValue) * 100)
          )
        : 0;

    index.Collective.Action.set(String(anchorActionId), {
      scope: "Collective",
      level: "Action",
      role: actionGoalRole,
      userRole: role, // Sponsor | Assignee (logged-in user)
      achievedValue: collectiveAchieved,
      targetValue,
      uom: actionUoM,
      progressPercent: collectiveProgress,
      contributors,
      sourceActionIds: collectiveActions.map(a => String(a._id)),
    });
  });

  return index;
}